import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from 'react-i18next';
import OverlayLoader from "../../../components/overlayLoader";
import { SkeletonRows } from "../../../components/skeleton";
import { getMaterialUsageReport, getMaterialSummary } from "../../../services/materials";
import { onQuerySuccess } from "../../../utils/queryResponse";
import { rsdFormat } from "../../../utils/helper";
import DatePicker from "../../../components/forms/formDatePicker";

const thStyle = {
    textAlign: 'left', fontWeight: 600, color: 'var(--muted)', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '.5px', padding: '12px 16px',
    borderBottom: '1.5px solid var(--line)', whiteSpace: 'nowrap',
};
const thRight = { ...thStyle, textAlign: 'right' };
const tdStyle = {
    padding: '14px 16px', borderBottom: '1px solid var(--line-soft)',
    fontSize: '13.5px', color: 'var(--ink)', verticalAlign: 'middle',
};
const tdRight = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const MaterialReportsPage = () => {

    const [rows, setRows] = useState([])
    const { t } = useTranslation();
    const [summary, setSummary] = useState(null)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)

    const { data: reportData, isFetching } = useQuery(
        ['material-usage-report', startDate, endDate],
        () => getMaterialUsageReport(startDate, endDate)
    )
    const { data: summaryData } = useQuery(
        ['material-summary', startDate, endDate],
        () => getMaterialSummary(startDate, endDate)
    )

    useEffect(() => {
        onQuerySuccess(reportData, (result) => setRows(result))
    }, [reportData])

    useEffect(() => {
        onQuerySuccess(summaryData, (result) => setSummary(result))
    }, [summaryData])

    return (
        <div className="sbr">
            <style>{`
                .mat-rpt-tbl tbody tr:hover td{background:var(--surface)}
                .mat-rpt-tbl tbody tr:last-child td{border-bottom:0}
                @media(max-width:599px){
                    .mat-kpi-grid{grid-template-columns:1fr 1fr !important;gap:8px !important;margin-bottom:12px !important}
                    .mat-kpi-grid .card-sbr{padding:12px !important}
                    .mat-kpi-grid .kpi-val{font-size:15px !important}
                    .mat-kpi-grid .kpi-lbl{font-size:10px !important}
                    .mat-rpt-tbl th,.mat-rpt-tbl td{padding:10px 10px !important;font-size:12px !important}
                }
            `}</style>
            <div className="dhead">
                <div>
                    <div className="ttl">{t('reports.materialReportsTitle')}</div>
                    <div className="sub">{t('reports.materialSubtitle')}</div>
                </div>
                <div className="act" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <DatePicker placeholderText="Start date" selected={startDate} onChange={setStartDate}
                        isClearable dateFormat="dd/MM/yyyy" />
                    <DatePicker placeholderText={t('reports.endDate')} selected={endDate} onChange={setEndDate}
                        isClearable dateFormat="dd/MM/yyyy" />
                </div>
            </div>

            {summary && (
                <div className="mat-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                    <div className="card-sbr" style={{ padding: '16px' }}>
                        <div className="sub kpi-lbl" style={{ fontSize: '11px', fontWeight: 700 }}>Total Revenue</div>
                        <div className="kpi-val" style={{ fontSize: '18px', fontWeight: 700 }}>{rsdFormat(summary.totalMaterialRevenue)}</div>
                    </div>
                    <div className="card-sbr" style={{ padding: '16px' }}>
                        <div className="sub kpi-lbl" style={{ fontSize: '11px', fontWeight: 700 }}>Total Cost</div>
                        <div className="kpi-val" style={{ fontSize: '18px', fontWeight: 700 }}>{rsdFormat(summary.totalMaterialCost)}</div>
                    </div>
                    <div className="card-sbr" style={{ padding: '16px' }}>
                        <div className="sub kpi-lbl" style={{ fontSize: '11px', fontWeight: 700 }}>Gross Profit</div>
                        <div className="kpi-val" style={{ fontSize: '18px', fontWeight: 700, color: summary.totalGrossProfit >= 0 ? 'var(--good)' : 'var(--bad)' }}>
                            {rsdFormat(summary.totalGrossProfit)}
                        </div>
                    </div>
                    <div className="card-sbr" style={{ padding: '16px' }}>
                        <div className="sub kpi-lbl" style={{ fontSize: '11px', fontWeight: 700 }}>Avg Margin</div>
                        <div className="kpi-val" style={{ fontSize: '18px', fontWeight: 700 }}>{summary.avgMargin?.toFixed(1)}%</div>
                    </div>
                </div>
            )}

            {isFetching && rows.length === 0 ? (
                <div className="card-sbr">
                    <SkeletonRows rows={5} widths={['20%', '15%', '15%', '15%', '15%', '10%']} />
                </div>
            ) : rows.length === 0 ? (
                <div className="card-sbr">
                    <div className="sbr-empty">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 20V10M12 20V4M6 20v-6" />
                        </svg>
                        {t('reports.noMaterialDataFound')}
                    </div>
                </div>
            ) : (
                <div className="card-sbr" style={{ padding: 0, overflow: 'auto' }}>
                    <table className="mat-rpt-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>{t('reports.materialHeader')}</th>
                                <th style={thStyle}>{t('reports.categoryHeader')}</th>
                                <th style={thStyle}>{t('reports.skuHeader')}</th>
                                <th style={thRight}>{t('reports.qtyUsed')}</th>
                                <th style={thRight}>{t('reports.revenueHeader')}</th>
                                <th style={thRight}>{t('reports.costHeader')}</th>
                                <th style={thRight}>{t('reports.profitHeader')}</th>
                                <th style={thRight}>{t('reports.marginHeader')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.materialId}>
                                    <td style={tdStyle}>
                                        <span style={{ fontWeight: 700, color: 'var(--ink)' }}>{row.materialName}</span>
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{ color: 'var(--text)', fontWeight: 500 }}>{row.category}</span>
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{ color: 'var(--muted)', fontSize: '12.5px' }}>{row.sku || '—'}</span>
                                    </td>
                                    <td style={tdRight}>
                                        <span style={{ fontWeight: 600 }}>{row.totalQuantityUsed}</span>
                                    </td>
                                    <td style={tdRight}>
                                        <span style={{ fontWeight: 600 }}>{rsdFormat(row.totalRevenue)}</span>
                                    </td>
                                    <td style={tdRight}>
                                        <span style={{ fontWeight: 600, color: 'var(--muted)' }}>{rsdFormat(row.totalCost)}</span>
                                    </td>
                                    <td style={tdRight}>
                                        <span style={{ fontWeight: 700, color: row.grossProfit >= 0 ? 'var(--good)' : 'var(--bad)' }}>
                                            {rsdFormat(row.grossProfit)}
                                        </span>
                                    </td>
                                    <td style={tdRight}>
                                        <span style={{ fontWeight: 600 }}>{row.margin?.toFixed(1)}%</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {isFetching && rows.length > 0 && (
                <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
                    <OverlayLoader />
                </div>
            )}
        </div>
    );
};

export default MaterialReportsPage;
